import { z } from "zod";
import { DOSE_OUTCOMES, DOSE_REASONS } from "./treatment-surface";

// TES-1151 S1 — GET patient-api/v1/treatment/overview, the read model behind
// "Min behandlingsöversikt". Built on the server: the app never computes a
// planned day, a status or a window.
//
// Ö-420/421 (Emelie, TES-1396): "Behandlingsöversikten ska gå tillbaka till
// den första verifierade behandlingsdagen som Ella har tillgång till … ingen
// godtycklig tolvveckorsgräns … systemet får inte skapa eller anta äldre
// doser som Ella saknar verifierade uppgifter om". So the response covers
// her whole history from `startsOn`, and the app may show a shorter window
// first. Planned days exist only from her first dose registered in Ella; a
// transfer patient's verified start may lie before it, and nothing is
// planned or assumed in between.

const ISO_CALENDAR_DATE = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD");

// The status of one planned dose day, derived on read and never stored:
//   due            — today's planned dose, not answered yet
//                    ("Dags för veckans dos");
//   not_registered — a passed planned day she never answered
//                    ("Ingen dos registrerad"); never "missed";
//   taken          — "Dosen är tagen", on `answer.takenOn`;
//   not_taken      — her own "Jag tog ingen dos".
export const TREATMENT_OVERVIEW_DAY_STATUSES = [
  "due",
  "not_registered",
  "taken",
  "not_taken",
] as const;
export type TreatmentOverviewDayStatus =
  (typeof TREATMENT_OVERVIEW_DAY_STATUSES)[number];

// Where `startsOn` comes from: her first dose registered in Ella (a new
// treatment, CP-12), or the doctor's verified or estimated start of her
// current treatment period (a prescription transfer, TES-734/TES-1398).
export const TREATMENT_OVERVIEW_START_SOURCES = [
  "first_dose",
  "treatment_start",
] as const;

// Her treatment now: active, paused by the doctor, out of a pause and
// waiting for the doctor's new start date (no planned day until then), or
// ended.
export const TREATMENT_OVERVIEW_STATES = [
  "active",
  "paused",
  "awaiting_restart",
  "ended",
] as const;
export type TreatmentOverviewState = (typeof TREATMENT_OVERVIEW_STATES)[number];

// Her standing answer to a planned dose (corrections replace it, Ö-417).
// `id` is the dose log row a symptom registered from the dose names
// (POST patient-api/v1/symptoms, `doseEventId`). `timing` compares the day
// she took it with the planned day. `correctable` is whether POST
// /treatment/dose/correction would accept a correction now: within
// `correctableUntil` (24 hours from her first answer) and while her
// schedule is live (not during a pause or after the treatment ended); after
// that the app offers the contact route.
export const treatmentOverviewAnswerSchema = z.object({
  id: z.string(),
  outcome: z.enum(DOSE_OUTCOMES),
  takenOn: ISO_CALENDAR_DATE.nullable(),
  timing: z.enum(["on_day", "late", "early"]).nullable(),
  reason: z.enum(DOSE_REASONS).nullable(),
  reasonNote: z.string().nullable(),
  correctable: z.boolean(),
  correctableUntil: z.string(),
});
export type TreatmentOverviewAnswer = z.infer<
  typeof treatmentOverviewAnswerSchema
>;

// One planned dose day up to today, oldest first, plus (TES-1527) the next
// planned day when she has already answered it: a dose she took before its
// planned day ("Registrera tagen dos"). `registrable`: POST
// /treatment/dose accepts an answer for it now (the open dose, or an
// earlier unanswered planned day outside a pause, Ö-416); `takenOnRange` is
// then the days "Dosen är tagen" may name, else null. A day a doctor's pause
// covered, or one after a pause before the doctor's new start date, is not a
// planned day and is not listed; the pause is.
export const treatmentOverviewDaySchema = z.object({
  plannedOn: ISO_CALENDAR_DATE,
  status: z.enum(TREATMENT_OVERVIEW_DAY_STATUSES),
  answer: treatmentOverviewAnswerSchema.nullable(),
  registrable: z.boolean(),
  takenOnRange: z
    .object({ earliest: ISO_CALENDAR_DATE, latest: ISO_CALENDAR_DATE })
    .nullable(),
});
export type TreatmentOverviewDay = z.infer<typeof treatmentOverviewDaySchema>;

// "Uppehåll enligt läkare" — a doctor's pause as its own period (Ö-418,
// Ö-424), oldest first. Stockholm dates; `to` null while it lasts.
// `awaitingRestart`: it has ended and the doctor has not yet set the new
// start date.
export const treatmentOverviewPauseSchema = z.object({
  from: ISO_CALENDAR_DATE.nullable(),
  to: ISO_CALENDAR_DATE.nullable(),
  endedBy: z.enum(["resumed", "ended"]).nullable(),
  awaitingRestart: z.boolean(),
});
export type TreatmentOverviewPause = z.infer<
  typeof treatmentOverviewPauseSchema
>;

// TES-1455 (Ö-687) — her first dose as registered: the date she says she
// took it, and whether POST /treatment/first-dose/correction would accept
// her own correction now (24 hours from her first registration, never
// extended by a correction); after that only the doctor corrects it.
// `correctedBy` is who gave the standing date when it is a correction, else
// null. TES-1522 (Ö-789 b): a correction recomputes her future planned days
// and check-ins from the corrected date; what already came stays. `kind`
// says which first dose it is, for the correction screen's copy (Ö-806):
// a new treatment's, or a prescription transfer's first dose "hos Ella".
// Optional for a build newer than the API it talks to.
export const treatmentOverviewFirstDoseSchema = z.object({
  kind: z.enum(["new_treatment", "transfer"]).optional(),
  takenOn: ISO_CALENDAR_DATE,
  correctable: z.boolean(),
  correctableUntil: z.string(),
  correctedBy: z.enum(["patient", "doctor"]).nullable(),
});
export type TreatmentOverviewFirstDose = z.infer<
  typeof treatmentOverviewFirstDoseSchema
>;

export const treatmentOverviewSchema = z.object({
  // Stockholm date the response was computed for.
  today: ISO_CALENDAR_DATE,
  // The first verified treatment day Ella has.
  startsOn: ISO_CALENDAR_DATE,
  startsFrom: z.enum(TREATMENT_OVERVIEW_START_SOURCES),
  // For a transfer start: exact or doctor-estimated; null otherwise.
  startPrecision: z.enum(["exact", "estimated"]).nullable(),
  // Her first dose registered in Ella (CP-12), null before it: the date her
  // schedule is anchored on, as corrected (TES-1522).
  firstDoseOn: ISO_CALENDAR_DATE.nullable(),
  // TES-1455 — her first dose's record (above). Null before it, and for a
  // first dose that has no dose log row. Optional for a build newer than
  // the API it talks to.
  firstDose: treatmentOverviewFirstDoseSchema.nullable().optional(),
  state: z.enum(TREATMENT_OVERVIEW_STATES),
  // The next planned dose day: the same value as "Din behandling"'s
  // nextDoseOn. Null without planned weekly doses now.
  nextPlannedOn: ISO_CALENDAR_DATE.nullable(),
  plannedDays: z.array(treatmentOverviewDaySchema),
  pauses: z.array(treatmentOverviewPauseSchema),
});
export type TreatmentOverview = z.infer<typeof treatmentOverviewSchema>;
